<template>
  <div class="audio-player bg-white rounded-lg shadow px-3 py-1 my-2 dark:bg-gray-700">
    <div class="flex items-center gap-3">
      <!-- 播放/暂停按钮 -->
      <button
        @click="togglePlay"
        class="text-2xl"
        :class="[
          isAudioReady
            ? 'text-indigo-600 cursor-pointer dark:text-indigo-200'
            : 'text-gray-400 cursor-not-allowed dark:text-gray-200',
        ]"
        :disabled="!isAudioReady"
      >
        <PauseIcon v-if="isPlaying" class="w-8 h-8" />
        <PlayIcon v-else class="w-8 h-8" />
      </button>

      <!-- 进度条 -->
      <input
        type="range"
        :value="currentTime"
        :max="duration"
        @input="onTimeUpdate"
        class="flex-1 h-2 bg-indigo-200 rounded-lg appearance-none cursor-pointer accent-indigo-400 dark:bg-gray-400"
      />
      <div class="justify-between text-xs text-gray-500 hidden sm:flex dark:text-gray-200">
        <span>{{ formatTime(currentTime) }} / {{ formatTime(duration) }}</span>
      </div>

      <!-- 控制面板 -->

      <!-- 播放速度 -->
      <div class="relative group items-center flex">
        <button class="text-sm text-gray-600 px-2 dark:text-gray-200">{{ playbackRate }}x</button>
        <div
          class="z-50 absolute hidden group-hover:block bg-white/60 rounded-lg shadow-lg p-2 right-0 bottom-full translate-x-[1.8rem]"
        >
          <div class="flex flex-col gap-2">
            <button
              v-for="rate in [0.5, 0.75, 1, 1.25, 1.5, 2]"
              :key="rate"
              :class="[
                'px-3 py-1 hover:bg-indigo-100 rounded',
                playbackRate === rate ? 'text-indigo-600' : 'text-gray-600',
              ]"
              @click="
                playbackRate = rate;
                onRateChange();
              "
            >
              {{ rate }}x
            </button>
          </div>
        </div>
      </div>

      <!-- 音量控制 -->
      <div class="relative group items-center hidden sm:flex">
        <button class="text-xl text-gray-600 dark:text-gray-200">
          <SpeakerWaveIcon class="w-5 h-5" />
        </button>
        <div
          class="z-50 absolute hidden group-hover:block bg-white/60 rounded-lg shadow right-0 bottom-full translate-x-[1.8rem]"
        >
          <input
            type="range"
            v-model="volume"
            min="0"
            max="1"
            step="0.1"
            class="h-24 -rotate-90 transform origin-bottom w-20 -translate-y-[2.6rem] translate-x-[3rem] accent-indigo-200 bg-indigo-200"
            @input="onVolumeChange"
          />
        </div>
      </div>

      <!-- 重复次数 -->
      <!-- <input
          type="number"
          v-model="repeatTimes"
          min="1"
          class="w-12 text-center border rounded"
        /> -->
    </div>

    <audio
      ref="audioRef"
      :src="audioUrl"
      @timeupdate="updateTime"
      @loadedmetadata="onLoadedMetadata"
      @ended="onEnded"
    ></audio>
  </div>
</template>

<script setup lang="ts">
import {
  PlayIcon,
  PauseIcon,
  SpeakerWaveIcon,
} from "@heroicons/vue/24/outline";
import { ref, onMounted, onUnmounted } from "vue";

const props = defineProps<{
  audioUrl: string;
}>();

const audioRef = ref<HTMLAudioElement>();
const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const volume = ref(1);
const playbackRate = ref(1);
const repeatTimes = ref(1);
const currentRepeat = ref(0);
const isAudioReady = ref(false);

// 添加音频加载状态检查
const checkAudioReady = () => {
  if (!audioRef.value || !props.audioUrl) {
    isAudioReady.value = false;
    return;
  }

  // 检查音频是否已加载且可以播放
  if (audioRef.value.readyState >= 2) {
    isAudioReady.value = true;
  }
};

// 修改播放控制函数
const togglePlay = () => {
  if (!audioRef.value || !isAudioReady.value) return;
  if (isPlaying.value) {
    audioRef.value.pause();
  } else {
    audioRef.value.play();
  }
  isPlaying.value = !isPlaying.value;
};

const playAudio = () => {
  if (!audioRef.value) return;
  audioRef.value.play();
  isPlaying.value = true;
};

const pauseAudio = () => {
  if (!audioRef.value) return;
  audioRef.value.pause();
  isPlaying.value = false;
};

const resetAudio = () => {
  if (!audioRef.value) return;
  audioRef.value.currentTime = 0;
  audioRef.value.pause();
  isPlaying.value = false;
};

// 时间格式化
const formatTime = (time: number) => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

// 事件处理器
const onTimeUpdate = (e: Event) => {
  if (!audioRef.value) return;
  const target = e.target as HTMLInputElement;
  audioRef.value.currentTime = Number(target.value);
};

const updateTime = () => {
  if (!audioRef.value) return;
  currentTime.value = audioRef.value.currentTime;
};

const onLoadedMetadata = () => {
  if (!audioRef.value) return;
  duration.value = audioRef.value.duration;
};

const onVolumeChange = () => {
  if (!audioRef.value) return;
  audioRef.value.volume = volume.value;
};

const onRateChange = () => {
  if (!audioRef.value) return;
  audioRef.value.playbackRate = playbackRate.value;
};

const onEnded = () => {
  currentRepeat.value++;
  if (currentRepeat.value < repeatTimes.value) {
    if (!audioRef.value) return;
    audioRef.value.currentTime = 0;
    audioRef.value.play();
  } else {
    isPlaying.value = false;
    currentRepeat.value = 0;
  }
};

// 添加音频加载事件监听
onMounted(() => {
  if (audioRef.value) {
    audioRef.value.addEventListener("loadeddata", checkAudioReady);
    audioRef.value.addEventListener("error", () => {
      isAudioReady.value = false;
    });
  }
});

// 清理事件监听
onUnmounted(() => {
  if (audioRef.value) {
    audioRef.value.removeEventListener("loadeddata", checkAudioReady);
    audioRef.value.pause();
  }
});

defineExpose({
  playAudio,
  pauseAudio,
  resetAudio,
});
</script>
